import {buildMentionAnchor} from './mentionUtils';

export interface MessageMention {
    userId: string;
    displayName: string;
}

export interface MessageRoomMention {
    roomId: string;
    name: string;
}

const FENCE = '```';

const CODE_LANGUAGE_PATTERN = /^[a-zA-Z0-9_+#.-]{1,32}$/;

const LIST_ITEM_PATTERN = /^[ \t]*-[ \t]+(.*)$/;

const INLINE_CODE_PATTERN = /`([^`]+)`/g;

const AUTOLINK_PATTERN = /(https?:\/\/(?!matrix\.to)[^\s<>]+)|((?:^|\s)(www\.[^\s<>]+))/gi;

const EMPHASIS_RULES: Array<{delimiter: string; tag: string}> = [
    {delimiter: '*', tag: 'strong'},
    {delimiter: '_', tag: 'em'},
    {delimiter: '~', tag: 'del'},
];

const emphasisPattern = (delimiter: string): RegExp => {
    const d = delimiter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(
        `(^|[^\\p{L}\\p{N}])${d}([^\\s${d}](?:[^${d}]*[^\\s${d}])?)${d}(?![\\p{L}\\p{N}])`,
        'gu'
    );
};

const EMPHASIS_PATTERNS = EMPHASIS_RULES.map(rule => ({
    tag: rule.tag,
    delimiter: rule.delimiter,
    pattern: emphasisPattern(rule.delimiter),
}));

const hasDelimiterPair = (text: string, delimiter: string): boolean => {
    const first = text.indexOf(delimiter);
    return first !== -1 && text.indexOf(delimiter, first + 1) !== -1;
};

const replaceEmphasis = (text: string, replacement: (tag: string) => string): string =>
    EMPHASIS_PATTERNS.reduce(
        (current, {tag, delimiter, pattern}) =>
            hasDelimiterPair(current, delimiter) ? current.replace(pattern, replacement(tag)) : current,
        text
    );

export const escapeHtml = (value: string): string => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

type Block =
    | {type: 'code'; language: string; content: string}
    | {type: 'list'; items: string[]}
    | {type: 'text'; lines: string[]};

const findFenceCloseIndices = (lines: string[]): number[] => {
    const indices: number[] = [];
    for (let i = 0; i < lines.length; i++) {
        if (lines[i].trim() === FENCE) indices.push(i);
    }
    return indices;
};

const parseBlocks = (message: string): Block[] => {
    const lines = message.split('\n');
    const blocks: Block[] = [];
    let textLines: string[] = [];

    const flushText = () => {
        if (textLines.length > 0) {
            blocks.push({type: 'text', lines: textLines});
            textLines = [];
        }
    };

    const closeIndices = findFenceCloseIndices(lines);
    let closePtr = 0;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        if (line.startsWith(FENCE)) {
            while (closePtr < closeIndices.length && closeIndices[closePtr] <= i) closePtr++;
            const closingIndex = closePtr < closeIndices.length ? closeIndices[closePtr] : -1;
            if (closingIndex !== -1) {
                const rawLanguage = line.slice(FENCE.length).trim();
                flushText();
                blocks.push({
                    type: 'code',
                    language: CODE_LANGUAGE_PATTERN.test(rawLanguage) ? rawLanguage : '',
                    content: lines.slice(i + 1, closingIndex).join('\n'),
                });
                i = closingIndex;
                continue;
            }
        }

        const listItem = LIST_ITEM_PATTERN.exec(line);
        if (listItem) {
            const items: string[] = [];
            let cursor = i;
            while (cursor < lines.length) {
                const nextItem = LIST_ITEM_PATTERN.exec(lines[cursor]);
                if (!nextItem) break;
                items.push(nextItem[1]);
                cursor++;
            }
            flushText();
            blocks.push({type: 'list', items});
            i = cursor - 1;
            continue;
        }

        textLines.push(line);
    }

    flushText();
    return blocks;
};

const renderEmphasis = (escapedText: string): string =>
    replaceEmphasis(escapedText, tag => `$1<${tag}>$2</${tag}>`);

const shieldMatches = (text: string, pattern: RegExp | null, next: (segment: string) => string): string => {
    if (!pattern) return next(text);

    let result = '';
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    pattern.lastIndex = 0;
    while ((match = pattern.exec(text)) !== null) {
        result += next(text.slice(lastIndex, match.index));
        result += match[0];
        lastIndex = match.index + match[0].length;
    }
    return result + next(text.slice(lastIndex));
};

const sortRoomMentionsByEscapedNameLength = (roomMentions: MessageRoomMention[]): MessageRoomMention[] =>
    roomMentions
        .filter(room => room.name)
        .sort((a, b) => escapeHtml(b.name).length - escapeHtml(a.name).length);

const sortMentionsByEscapedNameLength = (mentions: MessageMention[]): MessageMention[] =>
    [...mentions].sort((a, b) => escapeHtml(b.displayName).length - escapeHtml(a.displayName).length);

const buildMentionShieldPattern = (named: MessageMention[]): RegExp | null => {
    if (named.length === 0) return null;
    const alternation = named
        .map(mention => `${escapeRegExp(escapeHtml(`@${mention.displayName}`))}(?![\\p{L}\\p{N}])`)
        .join('|');
    return new RegExp(alternation, 'giu');
};

const buildRoomMentionPattern = (named: MessageRoomMention[]): RegExp | null => {
    if (named.length === 0) return null;
    return new RegExp(
        `#(?:${named.map(room => escapeRegExp(escapeHtml(room.name))).join('|')})(?!\\S)`,
        'g'
    );
};

interface ShieldRule {
    pattern: RegExp;
    render: (match: RegExpExecArray) => string;
}

const shieldOrderedRules = (text: string, rules: ShieldRule[], next: (segment: string) => string): string => {
    if (rules.length === 0) return next(text);

    const execRuleFrom = (rule: ShieldRule, from: number): RegExpExecArray | null => {
        rule.pattern.lastIndex = from;
        return rule.pattern.exec(text);
    };

    const cache: (RegExpExecArray | null)[] = rules.map(rule => execRuleFrom(rule, 0));

    let result = '';
    let cursor = 0;

    while (cursor <= text.length) {
        let winnerIndex = -1;

        for (let i = 0; i < rules.length; i++) {
            const candidate = cache[i];
            if (candidate && (winnerIndex === -1 || candidate.index < (cache[winnerIndex] as RegExpExecArray).index)) {
                winnerIndex = i;
            }
        }

        if (winnerIndex === -1) {
            return result + next(text.slice(cursor));
        }

        const match = cache[winnerIndex] as RegExpExecArray;
        result += next(text.slice(cursor, match.index));
        result += rules[winnerIndex].render(match);
        cursor = match.index + match[0].length;

        for (let i = 0; i < rules.length; i++) {
            const candidate = cache[i];
            if (candidate && candidate.index < cursor) {
                cache[i] = execRuleFrom(rules[i], cursor);
            }
        }
    }

    return result;
};

const renderInline = (
    text: string,
    mentionPattern: RegExp | null,
    roomMentionPattern: RegExp | null
): string => {
    const escaped = escapeHtml(text);

    const renderPlain = (segment: string): string =>
        shieldMatches(segment, AUTOLINK_PATTERN, renderEmphasis);

    const rules: ShieldRule[] = [];
    if (mentionPattern) rules.push({pattern: mentionPattern, render: match => match[0]});
    if (roomMentionPattern) rules.push({pattern: roomMentionPattern, render: match => match[0]});
    rules.push({pattern: INLINE_CODE_PATTERN, render: match => `<code>${match[1]}</code>`});

    return shieldOrderedRules(escaped, rules, renderPlain);
};

const renderBlock = (
    block: Block,
    mentionPattern: RegExp | null,
    roomMentionPattern: RegExp | null
): string => {
    switch (block.type) {
        case 'code': {
            const languageClass = block.language ? ` class="language-${escapeHtml(block.language)}"` : '';
            return `<pre><code${languageClass}>${escapeHtml(block.content)}</code></pre>`;
        }
        case 'list':
            return `<ul>${block.items.map(item => `<li>${renderInline(item, mentionPattern, roomMentionPattern)}</li>`).join('')}</ul>`;
        case 'text':
            return block.lines.map(line => renderInline(line, mentionPattern, roomMentionPattern)).join('<br>');
    }
};

export const renderMessageHtml = (
    message: string,
    mentions: MessageMention[] = [],
    roomMentions: MessageRoomMention[] = []
): string => {
    const mentionPattern = buildMentionShieldPattern(sortMentionsByEscapedNameLength(mentions));
    const roomMentionPattern = buildRoomMentionPattern(sortRoomMentionsByEscapedNameLength(roomMentions));
    return parseBlocks(message).map(block => renderBlock(block, mentionPattern, roomMentionPattern)).join('');
};

export const transformPlainTextSegments = (html: string, transform: (text: string) => string): string => {
    const skip = /<pre[\s\S]*?<\/pre>|<code[\s\S]*?<\/code>|<[^>]*>/g;
    let result = '';
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = skip.exec(html)) !== null) {
        result += transform(html.slice(lastIndex, match.index)) + match[0];
        lastIndex = match.index + match[0].length;
    }

    return result + transform(html.slice(lastIndex));
};

const applyMentions = (html: string, mentions: MessageMention[]): string => {
    const sorted = sortMentionsByEscapedNameLength(mentions);
    const pattern = buildMentionShieldPattern(sorted);
    if (!pattern) return html;

    return transformPlainTextSegments(html, text =>
        text.replace(pattern, match => {
            const mention = sorted.find(candidate => escapeHtml(`@${candidate.displayName}`).toLowerCase() === match.toLowerCase());
            return mention
                ? `<a href="https://matrix.to/#/${escapeHtml(mention.userId)}">${escapeHtml(`@${mention.displayName}`)}</a>`
                : match;
        })
    );
};

const applyRoomMentions = (html: string, roomMentions: MessageRoomMention[]): string => {
    const named = sortRoomMentionsByEscapedNameLength(roomMentions);
    const pattern = buildRoomMentionPattern(named);
    if (!pattern) return html;

    return transformPlainTextSegments(html, text =>
        text.replace(pattern, match => {
            const escapedName = match.slice(1);
            const room = named.find(candidate => escapeHtml(candidate.name) === escapedName);
            return room ? buildMentionAnchor(room.roomId, room.name) : match;
        })
    );
};

const applyAutolinks = (html: string): string =>
    transformPlainTextSegments(html, text =>
        text.replace(AUTOLINK_PATTERN, (match: string, httpUrl?: string, wwwUrl?: string) => {
            if (httpUrl) {
                return `<a href="${httpUrl}">${httpUrl}</a>`;
            }
            if (wwwUrl) {
                const url = wwwUrl.trim();
                const prefix = wwwUrl.startsWith(' ') ? ' ' : '';
                return `${prefix}<a href="https://${url}">${url}</a>`;
            }
            return match;
        })
    );

export const buildFormattedBody = (
    message: string,
    mentions: MessageMention[] = [],
    roomMentions: MessageRoomMention[] = []
): string | null => {
    const html = applyAutolinks(
        applyRoomMentions(applyMentions(renderMessageHtml(message, mentions, roomMentions), mentions), roomMentions)
    );
    return html === escapeHtml(message) ? null : html;
};

export interface BuiltTextMessageContent {
    msgtype: 'm.text';
    body: string;
    format?: string;
    formatted_body?: string;
    'm.mentions'?: {user_ids: string[]};
}

export const buildTextMessageContent = (
    message: string,
    mentions: MessageMention[] = [],
    roomMentions: MessageRoomMention[] = []
): BuiltTextMessageContent => {
    const content: BuiltTextMessageContent = {
        msgtype: 'm.text',
        body: message,
    };

    const formattedBody = buildFormattedBody(message, mentions, roomMentions);
    if (formattedBody !== null) {
        content.format = 'org.matrix.custom.html';
        content.formatted_body = formattedBody;
    }

    if (mentions.length > 0) {
        content['m.mentions'] = {user_ids: mentions.map(mention => mention.userId)};
    }

    return content;
};

export interface BuiltReplyMessageContent extends BuiltTextMessageContent {
    'm.relates_to': {'m.in_reply_to': {event_id: string}};
}

export const buildReplyMessageContent = (
    message: string,
    replyToEventId: string,
    mentions: MessageMention[] = [],
    roomMentions: MessageRoomMention[] = []
): BuiltReplyMessageContent => ({
    ...buildTextMessageContent(message, mentions, roomMentions),
    'm.relates_to': {'m.in_reply_to': {event_id: replyToEventId}},
});

const decodeHtmlEntities = (value: string): string => value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');

const stripHtmlTags = (html: string): string => html.replace(/<[^>]*>/g, '');

export const hasSendableContent = (
    message: string,
    mentions: MessageMention[] = [],
    roomMentions: MessageRoomMention[] = []
): boolean => {
    const trimmed = message.trim();
    if (!trimmed) {
        return false;
    }
    if (!/[`*_~]/.test(trimmed)) {
        return true;
    }
    const html = buildFormattedBody(message, mentions, roomMentions) ?? escapeHtml(message);
    return decodeHtmlEntities(stripHtmlTags(html)).trim().length > 0;
};

export const formatMessagePreview = (message: string): string => {
    const parts = parseBlocks(message).map(block => {
        switch (block.type) {
            case 'code':
                return block.content;
            case 'list':
                return block.items.map(item => `- ${stripInlineMarkers(item)}`).join(' ');
            case 'text':
                return block.lines.map(stripInlineMarkers).join(' ');
        }
    });

    return parts.join(' ').replace(/\s+/g, ' ').trim();
};

function stripInlineMarkers(text: string): string {
    let result = '';
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    const stripEmphasis = (value: string): string => replaceEmphasis(value, () => '$1$2');

    INLINE_CODE_PATTERN.lastIndex = 0;
    while ((match = INLINE_CODE_PATTERN.exec(text)) !== null) {
        result += stripEmphasis(text.slice(lastIndex, match.index));
        result += match[1];
        lastIndex = match.index + match[0].length;
    }

    return result + stripEmphasis(text.slice(lastIndex));
}
